import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [invalidLink, setInvalidLink] = useState(false);

  useEffect(() => {
    // Handle recovery token from email link
    const handleRecoveryToken = async () => {
      const hash = window.location.hash;
      console.log('UpdatePassword: Checking for recovery token', { hash });
      
      // Check if we have a recovery token in the hash
      if (hash.includes('type=recovery')) {
        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        
        console.log('UpdatePassword: Found recovery hash', { 
          hasAccessToken: !!accessToken, 
          hasRefreshToken: !!refreshToken 
        });
        
        if (accessToken && refreshToken) {
          try {
            // Set the session with the recovery tokens
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
              token_type: 'bearer',
              expires_in: 3600
            });
            
            if (error) {
              console.error('❌ UpdatePassword: Error setting session from recovery tokens:', error);
              setInvalidLink(true);
              setSessionLoading(false);
              return;
            }
            
            if (data?.session) {
              console.log('✅ UpdatePassword: Session established from recovery token');
              setSessionLoading(false);
              return;
            } else {
              console.error('❌ UpdatePassword: setSession succeeded but no session returned');
              setInvalidLink(true);
              setSessionLoading(false);
              return;
            }
          } catch (err) {
            console.error('❌ UpdatePassword: Exception while setting session:', err);
            setInvalidLink(true);
            setSessionLoading(false);
            return;
          }
        } else {
          console.error('❌ UpdatePassword: Recovery hash found but missing tokens', {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken
          });
          setInvalidLink(true);
          setSessionLoading(false);
          return;
        }
      }
      
      // No recovery hash - check if user already has a valid session
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.warn('⚠️  UpdatePassword: No recovery hash and no valid session found');
          setInvalidLink(true);
        } else {
          console.log('✅ UpdatePassword: Valid session found from existing auth');
        }
      } catch (err) {
        console.error('❌ UpdatePassword: Error checking session:', err);
        setInvalidLink(true);
      }
      
      setSessionLoading(false);
    };
    
    handleRecoveryToken();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const { error } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (error) throw error;

      setSuccess(true);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/authentication');
      }, 2000);
    } catch (error) {
      console.error('Password update error:', error);
      setErrors({
        general: error?.message || 'Failed to update password. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-foreground text-center mb-3">
              Password Updated!
            </h2>
            
            <p className="text-muted-foreground text-center mb-6">
              Your password has been successfully updated. Redirecting you to login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border text-center">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (invalidLink) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground text-center mb-3">
              Reset link is invalid or expired
            </h2>
            <p className="text-muted-foreground text-center mb-6">
              Please request a new password reset link and open it directly from your email on this device.
            </p>
            <Button
              fullWidth
              variant="default"
              onClick={() => navigate('/reset-password')}
            >
              Request new reset link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Lock" size={32} color="var(--color-primary)" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-foreground text-center mb-3">
            Create New Password
          </h2>
          
          <p className="text-muted-foreground text-center mb-6">
            Choose a strong password for your account.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              type="password"
              name="password"
              label="New Password"
              placeholder="Enter new password (min. 8 characters)"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required
              autoFocus
            />
            
            <Input
              type="password"
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Re-enter your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              required
            />
            
            {errors.general && (
              <div className="text-sm text-red-600 p-2 bg-red-50 rounded">
                {errors.general}
              </div>
            )}
            
            <Button
              type="submit"
              variant="default"
              loading={loading}
              fullWidth
              iconName="Check"
              iconPosition="right"
            >
              Update Password
            </Button>
          </form>
          
          <div className="mt-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
            <div className="flex items-start gap-3">
              <Icon name="Shield" size={18} className="text-primary mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Password Requirements</p>
                <p>Your password must be at least 8 characters long.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
